import React from 'react'
import styled, { css } from 'styled-components'

type Size = string | number

interface AvatarWrapperProps {
  size?: Size
}

interface AvatarAdditionalProps {
  className?: string
  'data-testid'?: string
  name: string
  src?: string
}

export type AvatarProps = AvatarWrapperProps & AvatarAdditionalProps

const AvatarWrapper = styled.span.attrs<AvatarWrapperProps>(props => ({
  style: {
    width: props.size,
    height: props.size,
    fontSize: props.size,
  },
}))<AvatarWrapperProps>`
  display: block;

  border-radius: 50%;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.boxShadow.subtle};
  overflow: hidden;

  ::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.border.dark.high};
    width: 100%;
    height: 100%;
  }
`

const absoluteFullItems = css`
  position: absolute;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`

export const AvatarPlaceholder = styled.span`
  ${absoluteFullItems};

  display: flex;

  justify-content: center;
  align-items: center;

  font-size: ${({ theme }) => theme.fontSizes.little};

  color: ${({ theme }) => theme.colors.text.light.base};
  background-color: ${({ theme }) => theme.colors.orange};
  text-transform: uppercase;
`

const AvatarImage = styled.img`
  ${absoluteFullItems};
  background-color: ${({ theme }) => theme.colors.background.light.secondary};

  object-fit: cover;
  object-position: center;
`

/* eslint-disable react/display-name */

/**
 * Avatar
 *
 * Shows the user avatar image if has the image source, if not the first letter of the user name
 *
 * @param props - Component Props.
 *
 * @param {string} [props.className] - Additional styling class for the AvatarWrapper.
 * @param {string} [props.name] - The user name.
 * @param {string} [props.src] - The source of the user image.
 * @param {Size} [props.size] - Size of the Avatar
 *
 * @returns {React.ReactElement} React element.
 *
 * @example
 *
 * // Example without Image
 * <Avatar name="Pieter" />
 *
 * // Example with Image
 * <Avatar name="Pieter" src="https://pieter.image.com" />
 *
 */
const Avatar: React.FC<AvatarProps> = React.forwardRef<
  HTMLElement,
  AvatarProps
>((props, ref) => {
  const {
    className,
    'data-testid': dataTestId,
    name,
    src,
    size,
    ...otherProps
  } = props

  let content
  if (!src) {
    content = (
      <AvatarPlaceholder data-testid={dataTestId}>
        {name.charAt(0)}
      </AvatarPlaceholder>
    )
  } else {
    content = <AvatarImage data-testid={dataTestId} src={src} alt={name} />
  }

  return (
    <AvatarWrapper className={className} size={size} ref={ref} {...otherProps}>
      {content}
    </AvatarWrapper>
  )
})

Avatar.defaultProps = {
  name: '',
  size: '28px',
}

export default Avatar
